/**
 * Guided subdomain history
 * ------------------------
 * Cloudflare serves guided.outschool.com/.dev with browser-visible paths like
 * `/intake`, while the origin request is rewritten to `/guided/intake`.
 *
 * React Router still matches `/guided/*`, so on guided hosts we keep two path
 * views in sync:
 *
 * - Router / app history: `/guided/...`
 * - Browser history API:  `/...` (no `/guided` prefix)
 *
 * Standard hosts never use this module.
 */

const GUIDED_PATH_PREFIX = "/guided";

export function isGuidedHost(host: string | undefined): boolean {
  if (!host) {
    return false;
  }

  // Match guided.outschool.com / guided.outschool.dev (and local guided.* hosts).
  // Do not match review-app hostnames that merely contain the word "guided".
  const hostname = host.toLowerCase().split(":")[0] ?? "";
  return hostname === "guided" || hostname.startsWith("guided.");
}

function browserHost(): string | undefined {
  return typeof window === "undefined" ? undefined : window.location.host;
}

function withGuidedPathPrefix(pathname: string): string {
  if (
    pathname === GUIDED_PATH_PREFIX ||
    pathname.startsWith(`${GUIDED_PATH_PREFIX}/`)
  ) {
    return pathname;
  }

  return pathname === "/"
    ? GUIDED_PATH_PREFIX
    : `${GUIDED_PATH_PREFIX}${pathname}`;
}

function withoutGuidedPathPrefix(pathname: string): string {
  if (pathname === GUIDED_PATH_PREFIX) {
    return "/";
  }
  if (pathname.startsWith(`${GUIDED_PATH_PREFIX}/`)) {
    return pathname.slice(GUIDED_PATH_PREFIX.length);
  }

  return pathname;
}

function mapPathInStringLocation(
  location: string,
  mapPathname: (pathname: string) => string,
): string {
  if (!location.startsWith("/")) {
    return location;
  }

  const pathEnd = location.search(/[?#]/);
  if (pathEnd === -1) {
    return mapPathname(location);
  }

  return `${mapPathname(location.slice(0, pathEnd))}${location.slice(pathEnd)}`;
}

function mapPathInLocation(
  location: any,
  mapPathname: (pathname: string) => string,
) {
  if (typeof location === "string") {
    return mapPathInStringLocation(location, mapPathname);
  }
  if (!location?.pathname) {
    return location;
  }

  return {
    ...location,
    pathname: mapPathname(location.pathname),
  };
}

function locationForRouter(location: any) {
  return mapPathInLocation(location, withGuidedPathPrefix);
}

function locationForBrowser(location: any) {
  return mapPathInLocation(location, withoutGuidedPathPrefix);
}

/**
 * Wraps history so React Router sees `/guided/...` while push/replace/createHref
 * write unprefixed browser URLs.
 */
export function createGuidedHostHistory(history: any) {
  const guidedHistory: any = {
    ...history,
    location: locationForRouter(history.location),
    push: (location: any, state: any) => {
      history.push(locationForBrowser(location), state);
    },
    replace: (location: any, state: any) => {
      history.replace(locationForBrowser(location), state);
    },
    createHref: (location: any) => {
      return history.createHref(locationForBrowser(location));
    },
    listen: (listener: any) => {
      return history.listen((location: any, action: any) =>
        listener(locationForRouter(location), action),
      );
    },
  };

  // Keep the copied `location` in sync after navigations.
  history.listen((location: any) => {
    guidedHistory.location = locationForRouter(location);
    ["length", "entries", "index", "action"].forEach(prop => {
      if (history.hasOwnProperty(prop)) {
        guidedHistory[prop] = history[prop];
      }
    });
  });

  return guidedHistory;
}

/**
 * If the current (or provided) host is a guided subdomain, wrap history with
 * path translation. Otherwise return history unchanged.
 */
export function maybeWrapGuidedHostHistory(
  history: any,
  { host }: { host?: string } = {},
) {
  if (!isGuidedHost(host ?? browserHost())) {
    return history;
  }

  return createGuidedHostHistory(history);
}

export const __testOnly = {
  isGuidedHost,
  locationForBrowser,
  locationForRouter,
  withGuidedPathPrefix,
  withoutGuidedPathPrefix,
};
